/**
 * The document context's caches — the old web's `useDocumentContext.ts`,
 * re-keyed for 2.5.
 *
 * What is kept: the type keys and their localised names, one item list per
 * type (`typeItemsByKey` — the `@` menu's second step and the enrichment the
 * send path reads), the last search, and an *epoch* that every fetch captures
 * so a result that lands after the caches were cleared is dropped rather than
 * resurrecting a stale document.
 *
 * What changed: 2.x had one agent, one Revit and a WebView2 bridge that pushed
 * "the document changed" events. 2.5 has several instances and no bridge, so
 *
 *   - the caches are keyed on the **selected instance's year** and cleared
 *     when it changes — a different Revit is a different context file;
 *   - a document change is detected by **re-reading the keys** whenever the
 *     `@` menu opens (`refresh`): the keys response is one small read, and if
 *     its document identity or `lastUpdated` moved, the per-type caches are
 *     dropped. That is the bridge's `identityChanged || freshGeneration` rule,
 *     driven by the moment it matters instead of by a push.
 *
 * `canBrowseDocumentContext` is the old hook's own no-bridge fallback:
 * `keysLoaded`. A 404 (no context, or the plugin's `.invalid` marker) leaves it
 * false and the `@` menu empty, which is the correct "no document" answer.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  createDocumentContextClient,
  documentIdentity,
  type DocumentContextClient,
  type DocumentContextItem,
  type DocumentContextSearchHit,
  type DocumentContextStatus,
} from "../lib/documentContext";

/** How often a rebuild's outcome is asked for, and how long before giving up on it. */
export const REBUILD_POLL_MS = 1000;
export const REBUILD_TIMEOUT_MS = 5 * 60 * 1000;
/** How often the status is read while the index has not settled (`shouldPollStatus`) — a small local file read. */
export const STATUS_SETTLE_POLL_MS = 3000;
import { hasExtractableParameters } from "../lib/mentionChipPresentation";
import { autoBuildKey, shouldPollStatus } from "../lib/revitIndexState";
import type { EnrichmentItem, MentionEnrichmentSource } from "../lib/mentionUtils";

export interface DocumentContextState {
  revitVersion: string | null;
  keys: string[];
  keyCounts: Record<string, number>;
  displayNames: Record<string, string>;
  keysLoaded: boolean;
  loadingKeys: boolean;
  documentName: string | null;
  /** The indexed document's session-scoped id, as the instance manifest reports it. */
  documentId: string | null;
  documentPath: string | null;
  lastUpdated: string | null;
  /** The plugin's index status (item BK), or null before the first read. */
  status: DocumentContextStatus | null;
  /** A rebuild was asked for and has not settled. */
  rebuilding: boolean;
  fetchStatus: () => Promise<void>;
  /**
   * Ask the bound instance to rebuild, wait for the plugin to stop building,
   * then re-read the context. False when nothing could be asked (no instance,
   * an old plugin) or the build did not end fresh.
   */
  rebuild: () => Promise<boolean>;
  typeItems: DocumentContextItem[];
  typeItemsByKey: Record<string, DocumentContextItem[]>;
  searchResults: DocumentContextSearchHit[];
  canBrowseDocumentContext: boolean;
  fetchKeys: () => Promise<{ keys: string[]; displayNames: Record<string, string> }>;
  /** Re-read the keys; drops the per-type caches when the document moved. */
  refresh: () => Promise<void>;
  fetchTypeItems: (type: string) => Promise<DocumentContextItem[]>;
  searchItems: (query: string) => Promise<DocumentContextSearchHit[]>;
  /** The cached item, synchronously — for chips and enrichment. */
  lookup: (type: string, id: string) => DocumentContextItem | null;
  extractable: (type: string, id: string) => boolean | null;
  enrichment: MentionEnrichmentSource;
}

export function useDocumentContext({
  revitVersion,
  token,
  orgId,
  targetSessionId = null,
  client: clientOverride,
}: {
  revitVersion: string | null;
  token: string | null;
  orgId: string | null;
  /** The bound instance — who a rebuild is asked of. */
  targetSessionId?: string | null;
  client?: DocumentContextClient;
}): DocumentContextState {
  const client = useMemo<DocumentContextClient | null>(() => {
    if (clientOverride) return clientOverride;
    if (!token) return null;
    return createDocumentContextClient({ token, organizationId: orgId });
  }, [clientOverride, token, orgId]);

  const [keys, setKeys] = useState<string[]>([]);
  const [keyCounts, setKeyCounts] = useState<Record<string, number>>({});
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [displayNames, setDisplayNames] = useState<Record<string, string>>({});
  const [keysLoaded, setKeysLoaded] = useState(false);
  const [loadingKeys, setLoadingKeys] = useState(false);
  const [documentName, setDocumentName] = useState<string | null>(null);
  const [documentId, setDocumentId] = useState<string | null>(null);
  const [documentPath, setDocumentPath] = useState<string | null>(null);
  const [status, setStatus] = useState<DocumentContextStatus | null>(null);
  const [rebuilding, setRebuilding] = useState(false);
  const [typeItems, setTypeItems] = useState<DocumentContextItem[]>([]);
  const [typeItemsByKey, setTypeItemsByKey] = useState<Record<string, DocumentContextItem[]>>({});
  const [searchResults, setSearchResults] = useState<DocumentContextSearchHit[]>([]);

  // The item cache is mirrored in a ref: `enrichment.lookup` is synchronous
  // and must see what `prefetch` just fetched in the same tick, before React
  // has re-rendered with the new state.
  const itemsRef = useRef<Record<string, DocumentContextItem[]>>({});
  // Keys fetches in flight. `loadingKeys` follows this count, and the count is
  // decremented unconditionally: a fetch that clears the caches mid-way (the
  // document moved) bumps the epoch, and a `finally` guarded on that epoch
  // would leave the flag stuck true — the "stuck at refreshing" bug.
  const keysInFlight = useRef(0);
  const autoBuiltRef = useRef<string | null>(null);
  const inflight = useRef(new Map<string, Promise<DocumentContextItem[]>>());
  const epoch = useRef(0);
  const identityRef = useRef<string | null>(null);
  const lastUpdatedRef = useRef<string | null>(null);

  const clearItemCaches = useCallback(() => {
    epoch.current += 1;
    inflight.current.clear();
    itemsRef.current = {};
    setTypeItems([]);
    setTypeItemsByKey({});
    setSearchResults([]);
  }, []);

  const clearAll = useCallback(() => {
    clearItemCaches();
    identityRef.current = null;
    lastUpdatedRef.current = null;
    setKeys([]);
    setKeyCounts({});
    setLastUpdated(null);
    setDisplayNames({});
    setKeysLoaded(false);
    setDocumentName(null);
    setDocumentId(null);
    setDocumentPath(null);
    setStatus(null);
  }, [clearItemCaches]);

  // A different instance (or a sign-out) is a different context file.
  useEffect(() => {
    clearAll();
  }, [revitVersion, client, clearAll]);

  const fetchKeys = useCallback(async () => {
    if (!client || !revitVersion) return { keys: [] as string[], displayNames: {} as Record<string, string> };
    const at = epoch.current;
    keysInFlight.current += 1;
    setLoadingKeys(true);
    try {
      const data = await client.keys(revitVersion);
      if (at !== epoch.current) return { keys: [], displayNames: {} };
      const identity = documentIdentity(data);
      const moved =
        (identity !== null && identityRef.current !== null && identity !== identityRef.current) ||
        (data.lastUpdated !== null && lastUpdatedRef.current !== null && data.lastUpdated !== lastUpdatedRef.current);
      if (moved) clearItemCaches();
      if (identity !== null) identityRef.current = identity;
      lastUpdatedRef.current = data.lastUpdated;
      setKeys(data.keys);
      setKeyCounts(data.counts);
      setLastUpdated(data.lastUpdated);
      setDisplayNames(data.displayNames);
      setDocumentName(data.documentName);
      setDocumentId(data.documentId);
      setDocumentPath(data.documentPath);
      setKeysLoaded(true);
      return { keys: data.keys, displayNames: data.displayNames };
    } catch {
      // No context (404 — no document, or the plugin's `.invalid` marker), or
      // an agent that is not there: the menu stays empty, and the next `@`
      // asks again.
      if (at === epoch.current) {
        setKeys([]);
        setKeysLoaded(false);
        setDocumentName(null);
        setDocumentId(null);
        setDocumentPath(null);
      }
      return { keys: [], displayNames: {} };
    } finally {
      keysInFlight.current = Math.max(0, keysInFlight.current - 1);
      setLoadingKeys(keysInFlight.current > 0);
    }
  }, [client, revitVersion, clearItemCaches]);

  const fetchStatus = useCallback(async () => {
    if (!client || !revitVersion) return;
    const at = epoch.current;
    try {
      const next = await client.status(revitVersion);
      if (at === epoch.current) setStatus(next);
    } catch {
      if (at === epoch.current) setStatus(null);
    }
  }, [client, revitVersion]);

  const refresh = useCallback(async () => {
    await Promise.all([fetchKeys(), fetchStatus()]);
  }, [fetchKeys, fetchStatus]);

  const rebuild = useCallback(async (): Promise<boolean> => {
    if (!client || !revitVersion || !targetSessionId) return false;
    const at = epoch.current;
    setRebuilding(true);
    try {
      await client.rebuild(targetSessionId);
      // The build runs on Revit's API thread and a large model takes a while:
      // poll the status until the plugin says it stopped building.
      const deadline = Date.now() + REBUILD_TIMEOUT_MS;
      let last: DocumentContextStatus | null = null;
      while (Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, REBUILD_POLL_MS));
        if (at !== epoch.current) return false;
        last = await client.status(revitVersion).catch(() => null);
        if (last) setStatus(last);
        if (last && last.state !== "building") break;
      }
      // Whatever it built is a different index: the per-type caches are stale.
      clearItemCaches();
      await fetchKeys();
      return last?.state === "fresh";
    } catch {
      return false;
    } finally {
      // Unconditional: `clearItemCaches` above bumped the epoch, so an epoch
      // guard here would never clear the flag and the panel would load forever.
      setRebuilding(false);
    }
  }, [client, revitVersion, targetSessionId, clearItemCaches, fetchKeys]);

  // The flow the owner asked for (2026-08-25): selecting an instance whose
  // plugin reports no index builds it **once**; after that only the user's
  // refresh rebuilds, and a change shows as the stale banner.
  useEffect(() => {
    const key = autoBuildKey(status, targetSessionId);
    if (!key || rebuilding || autoBuiltRef.current === key) return;
    autoBuiltRef.current = key;
    void rebuild();
  }, [status, targetSessionId, rebuilding, rebuild]);

  // Selecting an instance reads the status once; a model still opening then reads
  // `unavailable` and nothing would ask again. Keep reading until the index settles,
  // so the document's `missing` reaches the auto-build above (2026-10-05, R2024).
  const pollStatus = shouldPollStatus(status);
  useEffect(() => {
    if (!client || !revitVersion || !pollStatus) return;
    const id = window.setInterval(() => void fetchStatus(), STATUS_SETTLE_POLL_MS);
    return () => window.clearInterval(id);
  }, [client, revitVersion, pollStatus, fetchStatus]);

  // A build that finished elsewhere (the Revit Database panel, another view) is a new
  // index: re-read the keys when the status reports a build the keys have not seen.
  const builtAt = status?.state === "fresh" || status?.state === "stale" ? status.lastUpdated : null;
  useEffect(() => {
    if (builtAt && builtAt !== lastUpdatedRef.current) void fetchKeys();
  }, [builtAt, fetchKeys]);

  // Keys load as soon as there is an instance, so `canBrowseDocumentContext`
  // is true before the first `@` rather than one keystroke late.
  useEffect(() => {
    if (client && revitVersion) {
      void fetchKeys();
      void fetchStatus();
    }
  }, [client, revitVersion, fetchKeys, fetchStatus]);

  const fetchTypeItems = useCallback(
    async (type: string): Promise<DocumentContextItem[]> => {
      if (!client || !revitVersion || !type) return [];
      const cached = itemsRef.current[type];
      if (cached) {
        setTypeItems(cached);
        return cached;
      }
      const pending = inflight.current.get(type);
      if (pending) return pending;
      const at = epoch.current;
      const promise = client
        .typeItems(revitVersion, type)
        .then((items) => {
          if (at !== epoch.current) return [];
          itemsRef.current = { ...itemsRef.current, [type]: items };
          setTypeItemsByKey(itemsRef.current);
          setTypeItems(items);
          return items;
        })
        .catch(() => [] as DocumentContextItem[])
        .finally(() => {
          if (inflight.current.get(type) === promise) inflight.current.delete(type);
        });
      inflight.current.set(type, promise);
      return promise;
    },
    [client, revitVersion],
  );

  const searchItems = useCallback(
    async (query: string): Promise<DocumentContextSearchHit[]> => {
      if (!client || !revitVersion || !query.trim()) return [];
      const at = epoch.current;
      try {
        const hits = await client.search(revitVersion, query.trim());
        if (at !== epoch.current) return [];
        setSearchResults(hits);
        return hits;
      } catch {
        return [];
      }
    },
    [client, revitVersion],
  );

  const lookup = useCallback((type: string, id: string): DocumentContextItem | null => {
    const items = itemsRef.current[type];
    if (!items) return null;
    return items.find((item) => item.id === id) ?? null;
  }, []);

  // Reads `typeItemsByKey` (state) rather than the ref so a chip re-renders
  // when its type's items arrive.
  const extractable = useCallback(
    (type: string, id: string): boolean | null => {
      const items = typeItemsByKey[type];
      if (!items) return null;
      const hit = items.find((item) => item.id === id);
      return hit ? hasExtractableParameters(hit) : null;
    },
    [typeItemsByKey],
  );

  const enrichment = useMemo<MentionEnrichmentSource>(
    () => ({
      lookup: (type, id) => lookup(type, id) as EnrichmentItem | null,
      prefetch: async (type) => {
        await fetchTypeItems(type);
      },
    }),
    [lookup, fetchTypeItems],
  );

  return {
    revitVersion,
    keys,
    keyCounts,
    displayNames,
    keysLoaded,
    loadingKeys,
    documentName,
    documentId,
    documentPath,
    lastUpdated,
    status,
    rebuilding,
    fetchStatus,
    rebuild,
    typeItems,
    typeItemsByKey,
    searchResults,
    canBrowseDocumentContext: keysLoaded,
    fetchKeys,
    refresh,
    fetchTypeItems,
    searchItems,
    lookup,
    extractable,
    enrichment,
  };
}
